import React, { useEffect, useMemo, useState, useCallback } from "react";
import { collection, getDocs, doc, getDoc, QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { db } from "../../firebase";

interface ViewPlanProps {
  onClose: () => void;
}

type ItemType = "treatment" | "back_assessment" | "arm_assessment" | "knee_assessment";

type PlanItem = {
  type: ItemType;
  completed?: boolean;
};

type PlanDay = {
  items: PlanItem[];
};

type PlanDoc = {
  name: string;
  days: PlanDay[];
};

const ViewPlan2: React.FC<ViewPlanProps> = ({ onClose }) => {
  const [planNames, setPlanNames] = useState<string[]>([]);
  const [selectedPlanName, setSelectedPlanName] = useState<string>("");
  const [plan, setPlan] = useState<PlanDoc | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlanNames = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, "recovery_plans"));
      const names: string[] = [];
      snap.forEach((d: QueryDocumentSnapshot<DocumentData>) => {
        // Prefer doc.id to be the canonical plan name; fallback to data().name if present
        const data = d.data();
        names.push((data?.name as string) || d.id);
      });
      names.sort((a, b) => a.localeCompare(b));
      setPlanNames(names);
      // Auto-select first if nothing chosen
      if (!selectedPlanName && names.length > 0) {
        setSelectedPlanName(names[0]);
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load plan list.");
    } finally {
      setLoadingList(false);
    }
  }, [selectedPlanName]);

  const fetchPlan = useCallback(async (name: string) => {
    if (!name) return;
    setLoadingPlan(true);
    setError(null);
    try {
      const ref = doc(db, "recovery_plans", name);
      const ds = await getDoc(ref);
      if (!ds.exists()) {
        setPlan(null);
        setError(`Plan '${name}' not found.`);
      } else {
        const data = ds.data() as any;
        const normalized: PlanDoc = {
          name: (data?.name as string) || name,
          days: Array.isArray(data?.days)
            ? data.days.map((d: any) => ({
                items: Array.isArray(d?.items)
                  ? d.items.map((it: any) => ({
                      type: it?.type as ItemType,
                      completed: !!it?.completed,
                    }))
                  : [],
              }))
            : [],
        };
        setPlan(normalized);
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load plan.");
    } finally {
      setLoadingPlan(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanNames();
  }, [fetchPlanNames]);

  useEffect(() => {
    if (selectedPlanName) {
      fetchPlan(selectedPlanName);
    }
  }, [selectedPlanName, fetchPlan]);

  const legend = useMemo(
    () => [
      { label: "Treatment", className: "bg-purple-600" },
      { label: "Assessment", className: "bg-blue-600" },
    ],
    []
  );

  const itemDotClass = (t: ItemType) =>
    t === "treatment" ? "bg-purple-600" : "bg-blue-600";

  const itemLabel = (t: ItemType) => {
    switch (t) {
      case "treatment":
        return "Treatment";
      case "back_assessment":
        return "Back Assessment";
      case "arm_assessment":
        return "Arm Assessment";
      case "knee_assessment":
        return "Knee Assessment";
      default:
        return t;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="relative bg-white p-6 md:p-4 rounded-3xl shadow-2xl w-12/12 lg:w-4/5 max-h-[90vh] overflow-auto">
        <h2 className="text-2xl font-bold mb-4">View Recovery Plans</h2>

        <button
          className="btn btn-sm btn-circle absolute right-4 top-4"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <label className="block text-sm font-medium mb-1">Select Plan</label>
            <select
              className="select select-bordered min-w-[220px]"
              disabled={loadingList || planNames.length === 0}
              value={selectedPlanName}
              onChange={(e) => setSelectedPlanName(e.target.value)}
            >
              {planNames.length === 0 ? (
                <option value="" disabled>
                  {loadingList ? "Loading…" : "No plans found"}
                </option>
              ) : (
                planNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))
              )}
            </select>
          </div>

       

          {plan && (
            <span className="text-sm text-gray-600">
              {loadingPlan
                ? "Loading plan…"
                : `${plan.days.length} day${plan.days.length === 1 ? "" : "s"}`}
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 mb-4">
          {legend.map((l) => (
            <div key={l.label} className="flex items-center gap-2 text-sm text-gray-700">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${l.className}`} />
              {l.label}
            </div>
          ))}
        </div>

        {/* Grid of days: 7 across, wrap down */}
        {!plan || loadingPlan ? (
          <div className="text-sm text-gray-600">Select a plan to view its days.</div>
        ) : plan.days.length === 0 ? (
          <div className="text-sm text-gray-600">This plan has no days.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {plan.days.map((day, idx) => (
              <div
                key={`day-${idx}`}
                className="rounded-2xl border border-gray-200 p-3"
              >
                <div className="font-bold mb-2">Day {idx + 1}</div>
                {day.items.length === 0 ? (
                  <div className="text-xs text-gray-500">No items</div>
                ) : (
                  <ul className="space-y-1.5">
                    {day.items.map((it, i) => (
                      <li key={`day-${idx}-item-${i}`} className="flex items-center gap-2 text-sm">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${itemDotClass(it.type)}`}
                          title={it.type}
                        />
                        <span className="truncate">{itemLabel(it.type)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewPlan2;